import React from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import Modal from "@cloudscape-design/components/modal";
import Multiselect from "@cloudscape-design/components/multiselect";
import Pagination from "@cloudscape-design/components/pagination";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import Textarea from "@cloudscape-design/components/textarea";

const MAX_UIDS = 5000;
const MATCHES_PER_PAGE = 15;
const UID_MODAL_PAGE_SIZE = 50;
const FIELD_OPTIONS = [
	{ value: "ip", label: "IP" },
	{ value: "browser", label: "Browser" },
	{ value: "deviceType", label: "Device type" },
	{ value: "deviceBrand", label: "Device brand" },
	{ value: "deviceModel", label: "Device model" },
	{ value: "os", label: "OS" },
	{ value: "screen", label: "Screen" },
	{ value: "region", label: "Region" },
	{ value: "method", label: "Method" },
	{ value: "sessionType", label: "Session type" },
	{ value: "userAgent", label: "User-agent" },
	{ value: "verify", label: "Verify" },
	{ value: "login", label: "Login" },
	{ value: "referrer", label: "Referrer" },
];

function getFieldOption(value) {
	return FIELD_OPTIONS.find((option) => option.value === value);
}

function getFieldLabel(value) {
	return getFieldOption(value)?.label || value;
}

export default class PlayersAntiFraud extends React.PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			loaded: true,
			analyzed: false,
			uidText: "",
			items: [],
			players: {},
			summary: {
				requestedUidCount: 0,
				analyzedUidCount: 0,
				withoutRecentSessionCount: 0,
			},
			minMatches: "2",
			fields: [],
			search: "",
			currentPage: 1,
			sortingDescending: true,
			uidModal: {
				visible: false,
				item: null,
				search: "",
				currentPage: 1,
			},
			error: "",
		};
	}

	getUidInput(value = this.state.uidText) {
		const values = String(value)
			.split(/[\s,;]+/)
			.map((item) => item.trim())
			.filter((item) => item.length);
		const invalidValues = values.filter((item) => {
			if (!/^\d+$/.test(item)) return true;

			const uid = Number(item);
			return !Number.isSafeInteger(uid) || uid <= 0;
		});
		const uids = invalidValues.length ? [] : [...new Set(values.map((item) => Number(item)))];

		return {
			uids,
			invalidValues,
		};
	}

	getUidInputError() {
		const input = this.getUidInput();

		if (input.invalidValues.length) return "UIDs must be positive integers";
		if (!input.uids.length) return "Enter at least one UID";
		if (input.uids.length > MAX_UIDS) return `A maximum of ${MAX_UIDS} unique UIDs is allowed`;

		return "";
	}

	getThresholdError() {
		if (!/^\d+$/.test(String(this.state.minMatches))) {
			return "Enter an integer from 2 to 5000";
		}

		const minMatches = Number(this.state.minMatches);
		if (minMatches < 2 || minMatches > MAX_UIDS) {
			return "Enter an integer from 2 to 5000";
		}

		return "";
	}

	getFilteredItems() {
		const minMatches = Number(this.state.minMatches);
		const search = this.state.search.trim().toLocaleLowerCase("en-US");
		const items = this.state.items.filter((item) => {
			if (item.uidCount < minMatches) return false;
			if (this.state.fields.length && !this.state.fields.includes(item.field)) return false;
			if (!search.length) return true;

			return item.uids.some((uid) => {
				const player = this.state.players[uid] || {};
				const login = String(player.login || "").toLocaleLowerCase("en-US");
				const referrer = player.referrer === null || typeof player.referrer === "undefined" ? "" : String(player.referrer);

				return String(uid).includes(search) || login.includes(search) || referrer.includes(search);
			});
		});

		return items.sort((first, second) => {
			const countDifference = this.state.sortingDescending ? second.uidCount - first.uidCount : first.uidCount - second.uidCount;
			if (countDifference !== 0) return countDifference;

			const fieldDifference = first.field.localeCompare(second.field);
			if (fieldDifference !== 0) return fieldDifference;

			return String(first.value).localeCompare(String(second.value));
		});
	}

	getGroupKey(item) {
		return JSON.stringify([item.field, item.value]);
	}

	openUidModal(item) {
		this.setState({
			uidModal: {
				visible: true,
				item,
				search: "",
				currentPage: 1,
			},
		});
	}

	closeUidModal() {
		this.setState({
			uidModal: {
				visible: false,
				item: null,
				search: "",
				currentPage: 1,
			},
		});
	}

	getUidModalItems() {
		const item = this.state.uidModal.item;
		if (!item) return [];

		const search = this.state.uidModal.search.trim().toLocaleLowerCase("en-US");

		return item.uids
			.filter((uid) => {
				if (!search.length) return true;

				const player = this.state.players[uid] || {};
				const login = String(player.login || "").toLocaleLowerCase("en-US");
				const referrer = player.referrer === null || typeof player.referrer === "undefined" ? "" : String(player.referrer);

				return String(uid).includes(search) || login.includes(search) || referrer.includes(search);
			})
			.map((uid) => ({
				uid,
				login: this.state.players[uid]?.login || "",
				referrer: this.state.players[uid]?.referrer || null,
			}));
	}

	updateUidText(value) {
		this.setState({
			uidText: value,
			error: "",
		});
	}

	analyze = () => {
		const uidInputError = this.getUidInputError();
		const thresholdError = this.getThresholdError();

		if (uidInputError || thresholdError) {
			this.setState({ error: uidInputError || thresholdError });
			return;
		}

		const { uids } = this.getUidInput();
		this.setState({
			loaded: false,
			analyzed: false,
			items: [],
			players: {},
			currentPage: 1,
			uidModal: {
				visible: false,
				item: null,
				search: "",
				currentPage: 1,
			},
			error: "",
		});

		this.props.Interface.request("get", { class: "users", module: "usersSession", method: "analyze" }, { uids })
			.then((response) => {
				this.setState({
					loaded: true,
					analyzed: true,
					items: response.items || [],
					players: response.players || {},
					summary: response.summary || {
						requestedUidCount: uids.length,
						analyzedUidCount: 0,
						withoutRecentSessionCount: uids.length,
					},
					error: "",
				});
			})
			.catch((error) => {
				const errorText = error && typeof error.text !== "undefined" ? error.text : String(error || "Unknown error");

				this.setState({
					loaded: true,
					analyzed: false,
					error: errorText,
				});
			});
	};

	renderUidModal() {
		const item = this.state.uidModal.item;
		const items = this.getUidModalItems();
		const pagesCount = Math.ceil(items.length / UID_MODAL_PAGE_SIZE);
		const visibleItems = items.slice((this.state.uidModal.currentPage - 1) * UID_MODAL_PAGE_SIZE, this.state.uidModal.currentPage * UID_MODAL_PAGE_SIZE);

		return (
			<Modal
				visible={this.state.uidModal.visible}
				size="large"
				header="Matching UIDs"
				onDismiss={() => this.closeUidModal()}
				footer={
					<Box float="right">
						<Button onClick={() => this.closeUidModal()}>Close</Button>
					</Box>
				}>
				<SpaceBetween size="m">
					{item ? (
						<Box variant="p">
							<b>{getFieldLabel(item.field)}:</b> {item.value}
						</Box>
					) : (
						<></>
					)}
					<FormField label="Search by UID, login or referrer">
						<Input
							type="search"
							inputMode="search"
							value={this.state.uidModal.search}
							placeholder="Enter UID, login or referrer"
							onChange={({ detail }) =>
								this.setState({
									uidModal: {
										...this.state.uidModal,
										search: detail.value,
										currentPage: 1,
									},
								})
							}
						/>
					</FormField>
					<Table
						stickyHeader={true}
						wrapLines={true}
						trackBy="uid"
						columnDefinitions={[
							{
								id: "uid",
								header: "UID",
								cell: (player) => (
									<Link
										variant="normal"
										onFollow={() => {
											this.props.Interface.loadInfo(player.uid);
										}}>
										{player.uid}
									</Link>
								),
							},
							{ id: "login", header: "Login", cell: (player) => player.login || "—" },
							{ id: "referrer", header: "Referrer", cell: (player) => player.referrer || "—" },
						]}
						items={visibleItems}
						pagination={
							<Pagination
								currentPageIndex={this.state.uidModal.currentPage}
								pagesCount={pagesCount}
								onChange={({ detail }) =>
									this.setState({
										uidModal: {
											...this.state.uidModal,
											currentPage: detail.currentPageIndex,
										},
									})
								}
							/>
						}
						header={<Header counter={<>({items.length})</>}>UIDs</Header>}
						empty="No UIDs found"
					/>
				</SpaceBetween>
			</Modal>
		);
	}

	getMissingSessionsText() {
		const count = this.state.summary.withoutRecentSessionCount;

		if (count === 0) return "All requested UIDs have a session in the last three months.";
		if (count === 1) return "1 requested UID has no session in the last three months.";

		return `${count} requested UIDs have no session in the last three months.`;
	}

	render() {
		const uidInput = this.getUidInput();
		const uidInputError = this.getUidInputError();
		const thresholdError = this.getThresholdError();
		const filteredItems = thresholdError ? [] : this.getFilteredItems();
		const pagesCount = Math.ceil(filteredItems.length / MATCHES_PER_PAGE);
		const visibleItems = filteredItems.slice((this.state.currentPage - 1) * MATCHES_PER_PAGE, this.state.currentPage * MATCHES_PER_PAGE);

		return (
			<>
				{this.renderUidModal()}
				<SpaceBetween size="l">
					<Container
						header={
							<Header
								variant="h2"
								description="Find matching session and account attributes inside the provided UID list. Only the latest session from the last three months is analyzed."
								actions={
									<Button variant="primary" loading={!this.state.loaded} disabled={!this.state.loaded || Boolean(uidInputError) || Boolean(thresholdError)} onClick={this.analyze}>
										Analyze
									</Button>
								}>
								AntiFraud analysis
							</Header>
						}>
						<SpaceBetween size="m">
							<FormField label="UID list" description={`${uidInput.uids.length} unique UID${uidInput.uids.length === 1 ? "" : "s"} (maximum ${MAX_UIDS})`} errorText={this.state.uidText.length ? uidInputError : ""}>
								<Textarea disabled={!this.state.loaded} rows={3} resize="vertical" value={this.state.uidText} placeholder="UIDs separated by commas, spaces or new lines" onChange={({ detail }) => this.updateUidText(detail.value)} />
							</FormField>
							<div style={{ width: "100%", maxWidth: "240px" }}>
								<FormField label="Minimum matches" description="Default: 2 unique UIDs" errorText={thresholdError}>
									<Input disabled={!this.state.loaded} type="number" inputMode="numeric" value={this.state.minMatches} onChange={({ detail }) => this.setState({ minMatches: detail.value, currentPage: 1 })} />
								</FormField>
							</div>
						</SpaceBetween>
					</Container>

					{this.state.error ? (
						<Alert type="error" header="Unable to analyze players">
							{this.state.error}
						</Alert>
					) : (
						<></>
					)}

					{this.state.analyzed ? (
						<Alert type="info" header={`Analyzed ${this.state.summary.analyzedUidCount} of ${this.state.summary.requestedUidCount} UIDs`}>
							{this.getMissingSessionsText()}
						</Alert>
					) : (
						<></>
					)}

					<Table
						stickyHeader={true}
						wrapLines={true}
						trackBy={(item) => this.getGroupKey(item)}
						loading={!this.state.loaded}
						sortingColumn={{ sortingField: "uidCount" }}
						sortingDescending={this.state.sortingDescending}
						onSortingChange={({ detail }) => this.setState({ sortingDescending: detail.isDescending, currentPage: 1 })}
						columnDefinitions={[
							{ id: "field", header: "Attribute", cell: (item) => getFieldLabel(item.field) },
							{ id: "value", header: "Value", cell: (item) => item.value },
							{ id: "uidCount", header: "Unique UIDs", sortingField: "uidCount", cell: (item) => item.uidCount },
							{
								id: "uids",
								header: "UID list",
								cell: (item) => <Button onClick={() => this.openUidModal(item)}>{`View UIDs (${item.uidCount})`}</Button>,
							},
						]}
						items={visibleItems}
						pagination={<Pagination disabled={!this.state.loaded} currentPageIndex={this.state.currentPage} pagesCount={pagesCount} onChange={({ detail }) => this.setState({ currentPage: detail.currentPageIndex })} />}
						filter={
							<Box margin={{ bottom: "m" }}>
								<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
									<FormField label="Search by UID, login or referrer">
										<Input disabled={!this.state.loaded || !this.state.analyzed} type="search" inputMode="search" value={this.state.search} placeholder="Enter UID, login or referrer" onChange={({ detail }) => this.setState({ search: detail.value, currentPage: 1 })} />
									</FormField>
									<FormField label="Attribute">
										<Multiselect disabled={!this.state.loaded || !this.state.analyzed} selectedOptions={FIELD_OPTIONS.filter((option) => this.state.fields.includes(option.value))} options={FIELD_OPTIONS} placeholder="All attributes" selectedAriaLabel="Selected attributes" onChange={({ detail }) => this.setState({ fields: detail.selectedOptions.map((option) => option.value), currentPage: 1 })} />
									</FormField>
								</Grid>
							</Box>
						}
						header={<Header counter={<>({filteredItems.length})</>}>Matches</Header>}
						empty={this.state.analyzed ? "No matching attributes found" : "Enter a UID list and run the analysis"}
					/>
				</SpaceBetween>
			</>
		);
	}
}
